import { LDUser } from "launchdarkly-js-client-sdk";
import Cookies from "js-cookie";
import { decryptValue } from "./app-utils";
import { launchDarkly } from "./launchdarkly-service";

/**
 * Create a LaunchDarkly user context from the current session
 * @returns LDUser object with user information
 */
export const createUserContext = (): LDUser => {
	try {
		const userId = Cookies.get("userId");
		const emailCookie = Cookies.get("email");
		const userRole = Cookies.get("userRole");
		const businessUnit = Cookies.get("businessUnit");

		if (userId) {
			const rolePlain = userRole ? decryptValue(userRole) : "";
			// Create a user context with custom attributes
			return {
				key: decryptValue(userId),
				email: emailCookie ? decryptValue(emailCookie) : undefined,
				custom: {
					role: rolePlain || "unknown",
					businessUnit: businessUnit || "unknown",
				},
			};
		}
	} catch (error) {
		console.error("Error creating user context:", error);
	}

	// Return anonymous user if not logged in or error occurs
	return {
		key: "anonymous-user",
		anonymous: true,
	};
};

/**
 * Check if a feature flag is enabled
 * @param flagKey - The feature flag key
 * @param defaultValue - Default value if flag not found (default: false)
 * @returns Boolean indicating if feature is enabled
 */
export const isFeatureEnabled = (flagKey: string, defaultValue = false): boolean => {
	return launchDarkly.variation(flagKey, defaultValue);
};

/**
 * Get a feature flag value with type safety
 * @param flagKey - The feature flag key
 * @param defaultValue - Default value if flag not found
 * @returns The flag value
 */
export const getFeatureFlag = <T = any>(flagKey: string, defaultValue: T): T => {
	return launchDarkly.variation<T>(flagKey, defaultValue);
};

/**
 * Update user context when user logs in
 * Call this after successful login
 */
export const updateLaunchDarklyUserContext = async (): Promise<void> => {
	const userContext = createUserContext();
	await launchDarkly.identify(userContext);
};

/**
 * Reset to anonymous user when user logs out
 * Call this during logout
 */
export const resetLaunchDarklyUserContext = async (): Promise<void> => {
	const anonymousUser: LDUser = {
		key: "anonymous-user",
		anonymous: true,
	};
	await launchDarkly.identify(anonymousUser);
};
